<template>
  <div>
    <div
      v-if="user"
      class="font-weight-bold"
    >
      {{ user.email }}
    </div>
    <div v-else>
      <BFormInput
        v-model="email"
        :placeholder="$t('settings.emailAdress')"
        size="sm"
        autofocus
      />
    </div>
    <hr>
    <div
      class="d-flex"
      style="min-height: 450px"
    >
      <div class="w-50">
        <div
          v-for="type in permissionTypes"
          :key="type.key"
          class="role-type my-2 py-2"
          :class="{
            hoverable: type.key !== 'PayeeAdmin' && type.key !== 'MessageAdmin',
            active: selectedType === type.key
          }"
          @click="selectedType = type.key"
        >
          <div
            class="d-flex align-items-center"
          >
            <Checkbox
              v-if="type.key === 'PayeeAdmin'"
              :value.sync="payeeAdmin"
            />
            <Checkbox
              v-if="type.key === 'MessageAdmin'"
              :value.sync="messageAdmin"
            />
            <div
              style="width: 30px"
              class="text-center"
            >
              <i
                :class="type.icon"
                class="text-secondary"
              />
            </div>
            <div class="font-weight-bold">
              {{ type.text }}
            </div>
          </div>
          <div
            v-if="details[type.key]"
          >
            <div
              v-for="d in details[type.key]"
              :key="d"
              class="small text-primary ml-4"
            >
              <i
                :class="getScopeIcon(type.key)"
              />
              {{ getScopeName(type.key, d) }}
            </div>
          </div>
        </div>
      </div>
      <div class="w-50 pl-2">
        <div
          v-if="(selectedType === 'PayersView' || selectedType === 'ShopOrdersView')"
        >
          <div class="font-weight-bold mb-1">
            Wybierz grupy:
          </div>
          <GroupSelect
            :key="selectedType"
            :group-codes.sync="details[selectedType]"
            :show-select-all="true"
            :allow-rule-based-select="true"
          />
        </div>
        <div v-else-if="selectedType === 'BillingCategoryView'">
          <CategoriesMultiselect
            :selected.sync="details[selectedType]"
            :readonly="false"
          />
        </div>
        <div v-else-if="selectedType === 'ShopCatalogAdmin'">
          <div class="font-weight-bold mb-1">
            Wybierz katalogi:
          </div>
          <CatalogsMultiselect
            :selected.sync="details[selectedType]"
          />
        </div>
      </div>
    </div>
    <hr>
    <div class="d-flex justify-content-end">
      <button
        class="btn btn-secondary mr-3"
        style="width: 150px"
        @click="cancel"
      >
        Anuluj
      </button>
      <button
        class="btn btn-primary"
        style="width: 150px"
        :disabled="pending"
        @click="save"
      >
        Zapisz
      </button>
    </div>
  </div>
</template>

<script>
import GroupSelect from '@/components/payers/form/GroupSelect';
import CatalogsMultiselect from '@/components/settings/CatalogsMultiselect';
import CategoriesMultiselect from '@/components/settings/CategoriesMultiselect';
import Checkbox from '@/components/utils/Checkbox';
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    user: Object,
  },
  data: () => ({
    payeeAdmin: false,
    messageAdmin: false,
    selectedType: '',
    details: {},
    email: '',
    pending: false,
  }),
  components: {
    Checkbox,
    GroupSelect,
    CatalogsMultiselect,
    CategoriesMultiselect,
  },
  computed: {
    ...mapGetters([
      'payee',
      'payeeId',
      'groups',
      'roleTypes',
    ]),
    ...mapGetters('shop', ['catalogs']),
    permissionTypes() {
      return this.roleTypes;
    },
    permissionsResult() {
      const permissions = [];

      if (this.payeeAdmin) {
        permissions.push({
          type: 'PayeeAdmin',
          scope: this.payeeId,
        });
      }

      if (this.messageAdmin) {
        permissions.push({
          type: 'MessageAdmin',
          scope: this.payeeId,
        });
      }

      Object.keys(this.details)
        .forEach((key) => {
          this.details[key].forEach((scope) => {
            permissions.push({
              type: key,
              scope: `${this.payeeId}|${scope}`,
            });
          });
        });

      return permissions;
    },
  },
  methods: {
    ...mapActions([
      'setPayeeRoles',
    ]),
    cancel() {
      this.$emit('cancel');
    },
    getScopeIcon(key) {
      if (key === 'PayersView' || key === 'ShopOrdersView') {
        return 'fas fa-users';
      }

      if (key === 'BillingCategoryView') {
        return 'fas fa-box-dollar';
      }
      return '';
    },
    getScopeName(key, scope) {
      if (key === 'PayersView' || key === 'ShopOrdersView') {
        if (scope === '#all') {
          return this.$t('company.all');
        }

        return this.groups.find((x) => x.code === scope)?.name ?? '?';
      }

      if (key === 'BillingCategoryView') {
        return this.payee.categories.find((x) => x.id === scope)?.name ?? '?';
      }
      if (key === 'ShopCatalogAdmin') {
        return this.catalogs.find((x) => x.id === scope)?.name ?? '?';
      }

      return '';
    },
    save() {
      this.pending = true;
      this.setPayeeRoles({
        params: {
          tenantId: this.payee.ownerId,
        },
        data: {
          userId: this.user?.id,
          email: this.email,
          payeeId: this.payeeId,
          permissions: this.permissionsResult,
        },
      })
        .then(() => {
          this.$emit('refresh');
          this.$emit('cancel');
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  created() {
    this.email = this.user?.email || '';
    this.user?.list.forEach((x) => {
      if (x.type === 'PayeeAdmin') {
        this.payeeAdmin = true;
      } else if (x.type === 'MessageAdmin') {
        this.messageAdmin = true;
      } else {
        const scope = x.scope.split('|')[1];
        this.$set(this.details, x.type, [...(this.details[x.type] || []), scope]);
      }
    });
  },
};
</script>

<style lang="scss" scoped>

.role-type {
  border: 1px solid #e9ecef;
  padding: 3px 10px;
  font-size: 14px;
  border-radius: 10px;

  &.hoverable {
    cursor: pointer;

    &:hover {
      background-color: #f8f8f8;
    }

    &.active {
      background-color: rgba($blue, 0.2);
    }
  }

}
</style>
